/**
 * LUMIVIS 3D — minimal reveal-on-scroll utility.
 * No dependencies. Skips entirely for prefers-reduced-motion (the CSS itself
 * only ever hides [data-lumivis-reveal] under "prefers-reduced-motion:
 * no-preference", see lumivis-experience.css, so under reduced motion
 * content is already visible by default regardless of this script).
 * Marks [data-lumivis-reveal] elements with .is-visible once they enter the
 * viewport.
 *
 * Shopify can replace or extend the product grid after the initial page
 * load without a full navigation - sorting/filtering re-render the section
 * in place (results-list.js / paginated-list.js) and infinite-scroll
 * pagination appends fresh cards directly to the grid. Those new cards
 * carry [data-lumivis-reveal] too, but a one-shot querySelectorAll() at
 * load time (as this used to be) never sees them, and they would then sit
 * at opacity:0 forever with no observer watching them. To fix that without
 * a permanently expensive DOM scan: a single MutationObserver is used
 * project-wide (not one per grid/card), it only inspects the nodes actually
 * added in each mutation record (no re-querying the whole document), and
 * every node is registered at most once via the LUMIVIS_REVEAL_INIT_ATTR
 * marker - so re-running this against a node already handled is a no-op.
 */
(() => {
  const REVEAL_SELECTOR = '[data-lumivis-reveal]';
  const LUMIVIS_REVEAL_INIT_ATTR = 'data-lumivis-reveal-init';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const canObserve = 'IntersectionObserver' in window;

  /** @type {IntersectionObserver | null} */
  let intersectionObserver = null;
  /** Nodes currently being watched, so a late-arriving node still gets its own safety net. */
  const watchedNodes = new Set();

  function getIntersectionObserver() {
    if (intersectionObserver) return intersectionObserver;
    intersectionObserver = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            watchedNodes.delete(entry.target);
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: '0px 0px -10% 0px' }
    );
    return intersectionObserver;
  }

  // Registers one reveal node at most once (fail-open: reduced motion or no
  // IntersectionObserver support reveals it immediately instead of watching).
  function registerRevealNode(node) {
    if (node.hasAttribute(LUMIVIS_REVEAL_INIT_ATTR)) return;
    node.setAttribute(LUMIVIS_REVEAL_INIT_ATTR, '');

    if (reduceMotion || !canObserve) {
      node.classList.add('is-visible');
      return;
    }

    watchedNodes.add(node);
    getIntersectionObserver().observe(node);

    // Per-node safety net: guarantee visibility even if the node never
    // intersects (e.g. it starts and stays off the interaction path, or the
    // observer misfires) so content can never be stuck at opacity:0 -
    // whether it existed at page load or was added minutes later.
    window.setTimeout(() => {
      if (!watchedNodes.has(node)) return;
      node.classList.add('is-visible');
      watchedNodes.delete(node);
      intersectionObserver?.unobserve(node);
    }, 4000);
  }

  function registerAllRevealNodes(root) {
    root.querySelectorAll(REVEAL_SELECTOR).forEach(registerRevealNode);
  }

  registerAllRevealNodes(document);

  // Reduced motion never hides content in the first place (see CSS), so
  // there is nothing dynamically added content would need this observer
  // for - skip the ongoing watch entirely in that case.
  //
  // Scope: only <results-list> (assets/results-list.js, the paginated
  // product-grid component used on collection and search-results pages) -
  // that is the one place product cards are actually replaced/appended
  // after load (sort/filter re-render, infinite-scroll pagination). A
  // single MutationObserver instance is reused across every <results-list>
  // instance on the page (observe() can watch multiple targets) rather
  // than creating one observer per grid, and it is never re-created or
  // duplicated on subsequent calls.
  if (!reduceMotion && 'MutationObserver' in window) {
    const grids = document.querySelectorAll('results-list');
    if (grids.length > 0) {
      const gridMutationObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.type === 'childList') {
            mutation.addedNodes.forEach((added) => {
              if (!(added instanceof Element)) return;
              if (added.matches(REVEAL_SELECTOR)) registerRevealNode(added);
              added.querySelectorAll?.(REVEAL_SELECTOR).forEach(registerRevealNode);
            });
          } else if (mutation.type === 'attributes') {
            // Verified against the real draft theme (assets/morph.js /
            // idiomorph-style diffing): a re-sorted/filtered grid currently
            // reports every reused card as a fresh addedNodes insertion, so
            // this branch is not exercised today. It stays as a narrow,
            // cheap defense-in-depth: IF a future morph strategy instead
            // keeps a card's DOM node and only resets its attributes to the
            // server-rendered defaults (which would silently strip both
            // ".is-visible" and this attribute, since neither exists in the
            // server HTML), this catches exactly that and re-registers the
            // node - it only ever fires for the one attribute below, on
            // elements already matching REVEAL_SELECTOR.
            const target = mutation.target;
            if (target instanceof Element && target.matches(REVEAL_SELECTOR) && !target.hasAttribute(LUMIVIS_REVEAL_INIT_ATTR)) {
              registerRevealNode(target);
            }
          }
        }
      });
      grids.forEach((grid) =>
        gridMutationObserver.observe(grid, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: [LUMIVIS_REVEAL_INIT_ATTR],
        })
      );
    }
  }
})();

/**
 * LUMIVIS 3D — subtle pointer parallax for [data-lumivis-parallax] containers.
 * Desktop + fine-pointer only, skipped for touch and prefers-reduced-motion.
 * Sets --lumivis-mx / --lumivis-my (range -1..1) for CSS to consume; amplitude
 * stays deliberately small (see CSS) - never a "tilt effect", just a hint of depth.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const hasFinePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  const containers = document.querySelectorAll('[data-lumivis-parallax]');

  if (reduceMotion || !hasFinePointer || containers.length === 0) return;

  containers.forEach((container) => {
    let ticking = false;
    let lastEvent = null;

    container.addEventListener('pointermove', (event) => {
      lastEvent = event;
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        const rect = container.getBoundingClientRect();
        const x = ((lastEvent.clientX - rect.left) / rect.width) * 2 - 1;
        const y = ((lastEvent.clientY - rect.top) / rect.height) * 2 - 1;
        container.style.setProperty('--lumivis-mx', x.toFixed(3));
        container.style.setProperty('--lumivis-my', y.toFixed(3));
        ticking = false;
      });
    });

    container.addEventListener('pointerleave', () => {
      container.style.setProperty('--lumivis-mx', 0);
      container.style.setProperty('--lumivis-my', 0);
    });
  });
})();

/**
 * LUMIVIS 3D — scroll progress (0..1) for [data-lumivis-scroll-progress] elements,
 * measured across the element's own height. Drives the "Licht -> Form" hero
 * transformation as the visitor scrolls, not just once on load.
 * rAF-throttled, skipped for prefers-reduced-motion.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const targets = document.querySelectorAll('[data-lumivis-scroll-progress]');

  if (reduceMotion || targets.length === 0) return;

  let ticking = false;

  const update = () => {
    targets.forEach((el) => {
      const rect = el.getBoundingClientRect();
      const total = rect.height || 1;
      const progress = Math.min(1, Math.max(0, (0 - rect.top) / total));
      el.style.setProperty('--lumivis-scroll-progress', progress.toFixed(3));
    });
    ticking = false;
  };

  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    },
    { passive: true }
  );

  update();
})();

/**
 * LUMIVIS 3D — Phase J.1: sticky-storytelling stage switch.
 * For each [data-lumivis-sticky-track] (a tall track containing a sticky
 * stage + N [data-lumivis-stage] panels), computes scroll progress across
 * the track's own scrollable height and maps it to a discrete active stage
 * index — toggling .is-active on the matching stage and on any
 * [data-lumivis-stage-dot] progress markers.
 *
 * Skipped entirely for prefers-reduced-motion: under reduced motion the
 * section's own CSS keeps every stage in normal document flow (no
 * position:sticky, see lumivis-experience.css), so there is no "active
 * stage" concept to compute — all stages are simply readable in order.
 * rAF-throttled scroll handler only (no animation loop), so there is
 * nothing to separately pause off-screen.
 */
(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const tracks = document.querySelectorAll('[data-lumivis-sticky-track]');

  if (reduceMotion || tracks.length === 0) return;

  tracks.forEach((track) => {
    // [data-lumivis-stage] matches BOTH the visual frame AND the text panel
    // for each logical stage (two elements per stage, see
    // lumivis-process.liquid) - the stage COUNT therefore comes from the
    // dot markers (exactly one per logical stage), and each element is
    // matched to the active stage via its own data-lumivis-stage-index,
    // never by its position within the combined NodeList (that would pair
    // frames and panels off-by-one/desynced once panels start).
    const stageEls = track.querySelectorAll('[data-lumivis-stage]');
    const dots = track.querySelectorAll('[data-lumivis-stage-dot]');
    const stageCount = dots.length;
    if (stageCount === 0) return;

    let ticking = false;

    const update = () => {
      const rect = track.getBoundingClientRect();
      const total = rect.height - window.innerHeight;
      const progress = total > 0 ? Math.min(1, Math.max(0, -rect.top / total)) : 0;
      const activeIndex = Math.min(stageCount - 1, Math.floor(progress * stageCount));

      stageEls.forEach((stage) => {
        stage.classList.toggle('is-active', Number(stage.dataset.lumivisStageIndex) === activeIndex);
      });
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === activeIndex));
      track.style.setProperty('--lumivis-track-progress', progress.toFixed(3));
      ticking = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );

    update();
  });
})();

/**
 * LUMIVIS 3D — Phase J.1: pause continuous CSS animations while their
 * container is scrolled out of the viewport (performance budget: no
 * animation keeps running, no CPU/battery cost, off-screen). Sets
 * --lumivis-play-state ('running' | 'paused') as a custom property on
 * [data-lumivis-continuous-motion] containers; the CSS rules that actually
 * animate (e.g. the hero's breathing halo) read it via
 * "animation-play-state: var(--lumivis-play-state, running)". No effect
 * under prefers-reduced-motion, where those same animations are already
 * disabled entirely by the section's own CSS.
 */
(() => {
  const containers = document.querySelectorAll('[data-lumivis-continuous-motion]');
  if (!('IntersectionObserver' in window) || containers.length === 0) return;

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        entry.target.style.setProperty('--lumivis-play-state', entry.isIntersecting ? 'running' : 'paused');
      });
    },
    { threshold: 0 }
  );

  containers.forEach((container) => observer.observe(container));
})();
